import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AudioLanguage } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/Skeleton';
import { buttonVariants } from '@/components/ui/Button';
import { CONTENT_RATINGS } from '@gobingebox/shared';
import { ArrowLeft, Calendar, ChevronRight, Globe, Lock, Play } from '@/components/icons';
import { catalogApi, type PlaybackInfo, meApi, playbackApi } from '@/features/catalog/api';
import { useAuth } from '@/features/auth/AuthContext';

const LANG_LABEL: Record<AudioLanguage, string> = { ja: 'Japanese', en: 'English' };
const IDLE_MS = 8 * 60 * 1000; // auto-timeout after 8 min idle (releases the stream slot)
const ratingLabel = (r?: string | null) => CONTENT_RATINGS.find((x) => x.value === r)?.label ?? r;

/** Anti-piracy: block the player context menu (spec §7). DRM does the heavy lifting. */
function noContextMenu(e: React.MouseEvent) {
  e.preventDefault();
}

function PlayerFrame({ info, startAt }: { info: PlaybackInfo; startAt: number }) {
  const params = new URLSearchParams({ primaryColor: '3B82F6' });
  if (startAt > 0) params.set('startTime', `${startAt}s`);
  const src = `https://iframe.cloudflarestream.com/${info.src}?${params.toString()}`;
  return (
    <iframe
      title="BingeBox player"
      src={src}
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
      allowFullScreen
      className="absolute inset-0 h-full w-full border-0"
    />
  );
}

function StateCard({
  icon,
  title,
  body,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  body?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="absolute inset-0 grid place-items-center p-6 text-center">
      <div className="max-w-sm space-y-3">
        {icon && <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-surface-raised text-brand-soft">{icon}</div>}
        <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
        {body && <p className="text-sm text-ink-muted">{body}</p>}
        {children}
      </div>
    </div>
  );
}

export function EpisodePage() {
  const { slug = '', episode = '' } = useParams<{ slug: string; episode: string }>();
  const [params] = useSearchParams();
  const mode: 'preview' | 'full' = params.get('mode') === 'preview' ? 'preview' : 'full';
  const { user } = useAuth();

  const showQ = useQuery({ queryKey: ['show', slug], queryFn: () => catalogApi.show(slug) });
  const ep = showQ.data?.episodes.find((e) => String(e.number) === episode);

  const playbackQ = useQuery({
    queryKey: ['playback', ep?.id, mode, user?.id ?? 'guest'],
    queryFn: () => catalogApi.playback(ep!.id, mode),
    enabled: Boolean(ep),
  });

  const progressQ = useQuery({
    queryKey: ['progress', ep?.id],
    queryFn: () => meApi.getProgress(ep!.id),
    enabled: Boolean(ep && user && mode === 'full'),
  });

  const [audio, setAudio] = useState<AudioLanguage>('ja');
  const [streamError, setStreamError] = useState<string | null>(null);
  const [idle, setIdle] = useState(false);
  const [warned, setWarned] = useState(false);
  const lastActivity = useRef(Date.now());
  const info = playbackQ.data;
  const isReadyFull = info?.status === 'ready' && mode === 'full';

  // Reset the content-warning gate when switching episode/mode.
  useEffect(() => {
    setWarned(false);
    setIdle(false);
  }, [ep?.id, mode]);

  // Idle auto-timeout: after inactivity, pause + release the stream slot.
  useEffect(() => {
    if (!isReadyFull) return;
    const bump = () => {
      lastActivity.current = Date.now();
    };
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - lastActivity.current > IDLE_MS) setIdle(true);
    }, 30_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(timer);
    };
  }, [isReadyFull]);

  // Simultaneous-stream session: register on full playback, heartbeat, release.
  const sessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isReadyFull || idle) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;
    playbackApi
      .startSession(ep!.id)
      .then((s) => {
        if (cancelled) {
          void playbackApi.stopSession(s.sessionId);
          return;
        }
        sessionRef.current = s.sessionId;
        timer = setInterval(() => {
          if (sessionRef.current) void playbackApi.heartbeat(sessionRef.current).catch(() => undefined);
        }, 30_000);
      })
      .catch((err) => {
        setStreamError(
          err?.body?.error === 'too_many_streams'
            ? 'Too many active streams on your plan. Stop one to keep watching.'
            : null,
        );
      });
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (sessionRef.current) void playbackApi.stopSession(sessionRef.current);
      sessionRef.current = null;
    };
  }, [isReadyFull, ep, idle]);

  useEffect(() => {
    if (info?.audioLanguages?.length) setAudio(info.audioLanguages[0]!);
  }, [info]);

  if (showQ.isLoading) {
    return (
      <div className="container-page py-8">
        <Skeleton className="aspect-video w-full rounded-card" />
      </div>
    );
  }
  if (!showQ.data || !ep) {
    return (
      <div className="container-page py-20 text-center text-ink-muted">
        Episode not found.{' '}
        <Link to="/browse" className="font-semibold text-brand-soft underline">Back to Browse</Link>
      </div>
    );
  }

  const { show, episodes } = showQ.data;
  const nextEp = episodes.find((e) => e.season === ep.season && e.number === ep.number + 1);
  const startAt = mode === 'full' ? (progressQ.data?.progressSeconds ?? 0) : 0;

  return (
    <div className="container-page py-6">
      {/* top bar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to={`/shows/${show.slug}`} className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft size={16} />
          {show.title}
        </Link>
        <div className="flex items-center gap-3">
          <span className={cn('rounded-full px-2.5 py-1 text-2xs font-semibold uppercase tracking-wide', mode === 'preview' ? 'bg-surface-raised text-ink-muted' : 'bg-brand/15 text-brand-soft')}>
            {mode === 'preview' ? 'Preview' : 'Full episode'}
          </span>
          {info?.audioLanguages && info.audioLanguages.length > 0 && (
            <div className="inline-flex items-center gap-1 rounded-full border border-border bg-surface p-0.5" title="Audio language (switches in the player once tracks are uploaded)">
              <Globe size={14} className="ml-1.5 text-ink-faint" />
              {info.audioLanguages.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setAudio(l)}
                  className={cn('rounded-full px-2.5 py-1 text-xs font-semibold transition-colors', audio === l ? 'bg-brand-gradient text-white' : 'text-ink-muted hover:text-ink')}
                >
                  {LANG_LABEL[l]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* player */}
      <div
        className="relative aspect-video w-full overflow-hidden rounded-card border border-border bg-black"
        onContextMenu={noContextMenu}
      >
        {playbackQ.isLoading && <Skeleton className="absolute inset-0" />}

        {streamError ? (
          <StateCard icon={<Lock size={22} />} title="Too many streams" body={streamError} />
        ) : info?.status === 'ready' ? (
          idle ? (
            <StateCard title="Still watching?" body="Playback paused to free up your stream.">
              <button
                type="button"
                onClick={() => {
                  lastActivity.current = Date.now();
                  setIdle(false);
                }}
                className={buttonVariants({ size: 'md', className: 'mt-1' })}
              >
                <Play size={16} /> Resume
              </button>
            </StateCard>
          ) : info.rating && !warned ? (
            <StateCard
              title={`Rated ${ratingLabel(info.rating)}`}
              body="Viewer discretion advised. By continuing you confirm this content is appropriate for you."
            >
              <button
                type="button"
                onClick={() => setWarned(true)}
                className={buttonVariants({ size: 'md', className: 'mt-1' })}
              >
                Continue
              </button>
            </StateCard>
          ) : (
            <PlayerFrame info={info} startAt={startAt} />
          )
        ) : info?.status === 'age_restricted' ? (
          <StateCard
            icon={<Lock size={22} />}
            title={`Rated ${ratingLabel(info.rating)}`}
            body={`You must be at least ${info.requiredAge ?? ''} to watch this.`}
          />
        ) : info?.status === 'locked' ? (
          <StateCard
            icon={<Lock size={22} />}
            title="Subscribe to watch the full episode"
            body="Previews are free for everyone. Full episodes need an active plan."
          >
            <div className="flex justify-center gap-2 pt-1">
              <Link to="/plans" className={buttonVariants({ variant: 'primary', size: 'md' })}>See plans</Link>
              <Link to={`/shows/${show.slug}/${ep.number}?mode=preview`} className={buttonVariants({ variant: 'secondary', size: 'md' })}>
                Watch preview
              </Link>
            </div>
          </StateCard>
        ) : info?.status === 'upcoming' ? (
          <StateCard
            icon={<Calendar size={22} />}
            title="Not out yet"
            body={`This episode arrives ${info.releaseAt ? new Date(info.releaseAt).toLocaleDateString() : 'soon'}.`}
          />
        ) : info?.status === 'unavailable' ? (
          <StateCard
            icon={<Play size={22} />}
            title={mode === 'preview' ? 'No preview yet' : 'Video coming soon'}
            body={
              info.reason === 'no_preview'
                ? "A preview hasn't been added for this show yet."
                : 'This episode will play here once its video is uploaded from the Dev Panel.'
            }
          />
        ) : null}
      </div>

      {/* meta + next */}
      <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">
            S{ep.season} E{ep.number} · {ep.title}
          </h1>
          <p className="mt-1 max-w-prose text-sm text-ink-muted">{ep.description}</p>
        </div>
        {nextEp && (
          <Link
            to={`/shows/${show.slug}/${nextEp.number}`}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Next episode
            <ChevronRight size={16} />
          </Link>
        )}
      </div>
    </div>
  );
}
